"use client";

import { useCallback, useEffect, useState, type RefObject } from "react";

/** 소수점 카드 폭 때문에 끝에서도 1px 미만의 차이가 남을 수 있다. */
const EDGE_TOLERANCE = 1;

export function useSliderNavigation<T>(
  ref: RefObject<HTMLDivElement | null>,
  data: readonly T[],
  enabled: boolean,
) {
  const [edges, setEdges] = useState({ canPrevious: false, canNext: false });

  useEffect(() => {
    const track = ref.current;
    if (!enabled || !track) return;

    const measure = () => {
      const canPrevious = track.scrollLeft > EDGE_TOLERANCE;
      const canNext =
        track.scrollWidth - track.clientWidth - track.scrollLeft > EDGE_TOLERANCE;
      setEdges((current) =>
        current.canPrevious === canPrevious && current.canNext === canNext
          ? current
          : { canPrevious, canNext },
      );
    };

    measure();
    track.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    // 목록과 카드 크기가 바뀌어도 버튼 상태를 실제 스크롤 범위에 맞춘다.
    for (const child of track.children) observer.observe(child);

    return () => {
      track.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [ref, data, enabled]);

  const scroll = useCallback(
    (direction: -1 | 1) => {
      const track = ref.current;
      if (!track) return;
      track.scrollBy({
        // 이전 카드 일부를 남겨 넘긴 위치를 알아볼 수 있게 한다.
        left: direction * track.clientWidth * 0.8,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "instant"
          : "smooth",
      });
    },
    [ref],
  );

  return { ...edges, scroll };
}
